import type {
  ProductImportPreview,
  ProductImportProduct,
} from '../features/products/import/productImport';
import { dashboardApi } from '../lib/apiClient';
import type {
  Branch,
  BranchManualPaymentChannel,
  BranchPaymentSettings,
  BranchPricing,
  Category,
  ManualPaymentChannel,
  PaymentMethodConfig,
  Product,
  Promotion,
  StockMovement,
  SystemSettings,
  User,
} from '../types/domain';

export const upsertBranchPricing = (
  productId: string,
  branchId: string,
  price: number | null,
): Promise<BranchPricing[]> =>
  dashboardApi.upsertBranchPricing(productId, branchId, price);

export const upsertProduct = (product: Product): Promise<Product> =>
  dashboardApi.upsertProduct(product);

export const deleteProduct = (productId: string): Promise<void> =>
  dashboardApi.deleteProduct(productId);

export const upsertCategory = (category: Category): Promise<Category> =>
  dashboardApi.upsertCategory(category);

export interface ProductImportApplyInput {
  branchId: string;
  preview: ProductImportPreview;
}

export interface ProductImportApplyResult {
  productCount: number;
  categoryCount: number;
  openingStockCount: number;
}

const normalizedText = (value: string): string =>
  value.trim().normalize('NFKC');

const slugId = (prefix: string, value: string): string =>
  `${prefix}-${
    value
      .trim()
      .normalize('NFKC')
      .toLowerCase()
      .replace(/[^a-z0-9ก-๙]+/gi, '-')
      .replace(/^-|-$/g, '') || 'general'
  }`;

const toProduct = (
  item: ProductImportProduct,
  categoryId: string,
): Product => ({
  id: item.id,
  name: item.name,
  categoryId,
  baseUnit: item.baseUnit,
  baseUnitId:
    item.units.find((unit) => unit.factor === 1)?.id ?? item.units[0]?.id,
  units: item.units.map((unit) => ({
    id: unit.id,
    code: unit.code,
    unit: unit.unit,
    factor: unit.factor,
    price: unit.price,
    barcode: unit.barcode,
    barcodeId: unit.barcodeId,
  })),
  active: item.active,
  minStock: item.minStock,
  cost: item.cost,
});

export async function importProductsFromPreview(
  input: ProductImportApplyInput,
): Promise<ProductImportApplyResult> {
  if (!input.branchId) throw new Error('กรุณาเลือกสาขาก่อนนำเข้า');
  if (input.preview.errors.length > 0) {
    throw new Error('แก้ไขข้อผิดพลาดในไฟล์ Excel ก่อนนำเข้า');
  }

  const context = { branchId: input.branchId };
  const [currentCategories, currentProducts, currentStock] = await Promise.all([
    dashboardApi.listCategories(context),
    dashboardApi.listProducts(context),
    dashboardApi.listStockByBranch(context),
  ]);
  const barcodeOwner = new Map<string, Product>();
  for (const product of currentProducts) {
    for (const unit of product.units) {
      if (unit.barcode) barcodeOwner.set(unit.barcode, product);
    }
  }
  for (const product of input.preview.products) {
    for (const unit of product.units) {
      if (!unit.barcode) continue;
      const existing = barcodeOwner.get(unit.barcode);
      if (existing && existing.id !== product.id) {
        throw new Error(
          `บาร์โค้ด ${unit.barcode} ถูกใช้กับสินค้าเดิมแล้ว: ${existing.name}`,
        );
      }
    }
  }

  const byCategoryName = new Map(
    currentCategories.map((category) => [
      normalizedText(category.name),
      category,
    ]),
  );
  const usedCategoryIds = new Set(
    currentCategories.map((category) => category.id),
  );
  const currentQty = new Map(
    currentStock.map((row) => [row.productId, row.onHand]),
  );
  const touchedCategories = new Set<string>();
  let openingStockCount = 0;

  for (const item of input.preview.products) {
    const categoryName = normalizedText(item.categoryName);
    let category = byCategoryName.get(categoryName);
    if (!category) {
      const baseId = slugId('cat', item.categoryName);
      let id = baseId;
      for (let suffix = 2; usedCategoryIds.has(id); suffix += 1) {
        id = `${baseId}-${suffix}`;
      }
      category = await dashboardApi.upsertCategory(
        { id, name: item.categoryName },
        context,
      );
      byCategoryName.set(categoryName, category);
      usedCategoryIds.add(category.id);
    }
    touchedCategories.add(category.id);
    await dashboardApi.upsertProduct(toProduct(item, category.id), context);

    if (!item.openingStockSpecified && item.openingStockBaseQty <= 0) continue;
    const baseUnit =
      item.units.find((unit) => unit.factor === 1) ?? item.units[0];
    if (!baseUnit?.id) {
      throw new Error(`${item.name}: ไม่พบหน่วยฐานสำหรับบันทึกสต๊อกตั้งต้น`);
    }
    const before = currentQty.get(item.id) ?? 0;
    const quantity = item.openingStockBaseQty - before;
    if (quantity === 0) continue;
    const isReceive = quantity > 0;
    const idempotencyKey = `opening-stock:${input.branchId}:${item.id}:${before}:${item.openingStockBaseQty}:${quantity}`;
    const requestId =
      idempotencyKey.length <= 120
        ? idempotencyKey
        : `os:${input.branchId}:${item.id.substring(0, 40)}:${before}:${quantity}`;
    await dashboardApi.addStockMovement({
      branchId: input.branchId,
      productId: item.id,
      requestId,
      unitId: baseUnit.id,
      type: isReceive ? 'receive' : 'adjust',
      quantity,
      ...(isReceive ? { unitCost: item.cost } : {}),
      reason: 'ยอดตั้งต้นจาก Excel import',
    });
    currentQty.set(item.id, item.openingStockBaseQty);
    openingStockCount += 1;
  }

  return {
    productCount: input.preview.products.length,
    categoryCount: touchedCategories.size,
    openingStockCount,
  };
}

export async function addStockMovement(
  movement: Omit<StockMovement, 'id' | 'at'> & {
    requestId?: string;
    unitId?: string;
  },
): Promise<StockMovement> {
  if (movement.type !== 'receive' && movement.type !== 'adjust') {
    throw new Error(`Unsupported Dashboard stock movement: ${movement.type}`);
  }
  if (!movement.unitId)
    throw new Error('unitId is required for stock movements');
  if (movement.type === 'receive' && movement.unitCost === undefined) {
    throw new Error('unitCost is required for receive');
  }
  await dashboardApi.addStockMovement({
    branchId: movement.branchId,
    type: movement.type,
    productId: movement.productId,
    requestId: movement.requestId ?? crypto.randomUUID(),
    unitId: movement.unitId,
    quantity: movement.qty,
    ...(movement.type === 'receive' ? { unitCost: movement.unitCost } : {}),
    reason: movement.reason ?? '',
  });
  return { ...movement, id: 'api', at: Date.now() } as StockMovement;
}

export const upsertUser = (user: User): Promise<User> =>
  dashboardApi.upsertUser(
    user.id ? user : { ...user, id: `u-${crypto.randomUUID()}` },
  );
export const upsertBranch = (branch: Branch): Promise<Branch> =>
  dashboardApi.upsertBranch(
    branch.id ? branch : { ...branch, id: `b-${crypto.randomUUID()}` },
  );
export const upsertPromotion = (promotion: Promotion): Promise<Promotion> =>
  dashboardApi.upsertPromotion(
    promotion.id
      ? promotion
      : { ...promotion, id: `pr-${crypto.randomUUID()}` },
  );
export const upsertPaymentMethod = (
  method: PaymentMethodConfig,
): Promise<PaymentMethodConfig> => dashboardApi.upsertPaymentMethod(method);
export const saveBranchPaymentSettings = (
  settings: BranchPaymentSettings,
): Promise<BranchPaymentSettings> =>
  dashboardApi.saveBranchPaymentSettings(settings);
export const upsertManualPaymentChannel = (
  channel: ManualPaymentChannel,
): Promise<ManualPaymentChannel> =>
  dashboardApi.upsertManualPaymentChannel(channel);
export const upsertBranchManualPaymentChannel = (
  assignment: BranchManualPaymentChannel,
): Promise<BranchManualPaymentChannel> =>
  dashboardApi.upsertBranchManualPaymentChannel(assignment);
export const saveSettings = (
  settings: SystemSettings,
): Promise<SystemSettings> => dashboardApi.saveSettings(settings);
